import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, UploadCloud, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CircularProgress from './CircularProgress';
import useCVAutoFill from '../hooks/useCVAutoFill';
import { useNavigate } from 'react-router-dom';

const CVSelectionModal = ({ isOpen, onClose, uploadCount = 0, lastUpload }) => {
    const [view, setView] = useState('UPLOAD'); // UPLOAD, LOADING, SUCCESS
    const { triggerParsing, triggerMockParsing } = useCVAutoFill();
    const navigate = useNavigate();
    const [parsedData, setParsedData] = useState(null);
    const [isParsingComplete, setIsParsingComplete] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles?.length > 0) {
            setView('LOADING');
            setIsParsingComplete(false);
            try {
                const countForUpdate = uploadCount || 0;
                const data = await triggerParsing(acceptedFiles[0], countForUpdate);
                setParsedData(data);
                setIsParsingComplete(true); // Signal completion to spinner
                setUploadError(null);
            } catch (e) {
                console.error("Parsing failed", e);
                // Capture the error details to display to the user
                const errorMessage = e instanceof Error ? e.message : String(e);
                setUploadError(`Erreur lors de l'analyse : ${errorMessage}`);
                setView('UPLOAD');
                setIsParsingComplete(false);
            }
        }
    }, [triggerParsing, uploadCount, lastUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false
    });

    const handleProgressComplete = () => {
        // Redirect to profile with data
        onClose();
        // Reset view for next time
        setTimeout(() => {
            setView('UPLOAD');
            setIsParsingComplete(false);
            setUploadError(null);
        }, 500);
        navigate('/profil', { state: { cvData: parsedData } });
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl overflow-hidden border border-white/20"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-100">
                        <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                            <Sparkles className="text-blue-600" size={24} />
                            Mise à jour Intelligente
                        </h2>
                        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                            <X size={20} className="text-slate-400" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-8 min-h-[400px] flex flex-col items-center justify-center">
                        {view === 'UPLOAD' && (
                            <div className="w-full flex flex-col gap-4">
                                {uploadError && (
                                    <div className="w-full p-4 bg-red-50 text-red-600 rounded-2xl border border-red-200 text-sm font-medium">
                                        <p className="font-bold mb-1">Détails de l'erreur (pour le débogage) :</p>
                                        <p className="break-all whitespace-pre-wrap">{uploadError}</p>
                                    </div>
                                )}
                                <div
                                    {...getRootProps()}
                                    className={`w-full h-64 border-3 border-dashed rounded-3xl flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-300 ${isDragActive
                                        ? 'border-blue-500 bg-blue-50/50 scale-[1.02]'
                                        : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50'
                                        }`}
                                >
                                    <input {...getInputProps()} />
                                    <div className={`p-4 rounded-full ${isDragActive ? 'bg-blue-100' : 'bg-slate-100'}`}>
                                        <UploadCloud size={32} className={isDragActive ? 'text-blue-600' : 'text-slate-400'} />
                                    </div>
                                    <div className="text-center space-y-1">
                                        <p className="text-lg font-bold text-slate-700">
                                            {isDragActive ? 'Relâchez le CV ici' : 'Glissez votre CV ici'}
                                        </p>
                                        <p className="text-sm text-slate-400 font-medium">ou cliquez pour parcourir (PDF)</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {view === 'LOADING' && (
                            <CircularProgress
                                duration={11000}
                                isLoading={!isParsingComplete}
                                onComplete={handleProgressComplete}
                            />
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default CVSelectionModal;
